import {
  AudioLines,
  Download,
  Gauge,
  Languages,
  Pause,
  PenLine,
  Play,
  Quote,
  RotateCcw,
  ScrollText,
  SlidersHorizontal,
  Sparkles,
  UserRound,
  Users,
  Volume2,
  Wand2
} from "lucide-react";
import { type ClipboardEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

type ToneKey =
  | "neutral"
  | "warm"
  | "gentle"
  | "bright"
  | "calm"
  | "serious"
  | "dramatic"
  | "brisk"
  | "whisper"
  | "sad"
  | "angry"
  | "hopeful"
  | "empathetic"
  | "lyrical"
  | "newscast"
  | "assistant";

type PlaybackState = "idle" | "playing" | "paused";
type AudioRequestMode = "full" | "segment";

interface ToneProfile {
  label: string;
  rate: number;
  pitch: number;
  volume: number;
}

interface RoleConfig {
  name: string;
  color: string;
  tone: ToneKey;
  rate: number;
  pitch: number;
  volume: number;
  azureVoice?: string;
  azureLang?: string;
  azureStyle?: string;
  azureRole?: string;
  styleDegree?: number;
  analysisSource?: "gemini" | "fallback" | "manual";
  analysisReason?: string;
}

interface Segment {
  id: string;
  role: string;
  text: string;
  lang: string;
  tone?: ToneKey;
  rate?: number;
}

interface AzureVoice {
  name: string;
  locale: string;
  displayName?: string;
  localName?: string;
  gender?: string;
  styles?: string[];
  roles?: string[];
  freeTier?: boolean;
}

interface ProviderStatus {
  azure: {
    configured: boolean;
    quotaGuard: boolean;
    regionConfigured: boolean;
  };
  limits: {
    maxPerRequest: number;
    monthlyLimit: number;
    usedThisMonth: number;
    remainingThisMonth: number;
  };
  cloudflareWorkersAi: {
    configured: boolean;
    enabled: boolean;
  };
}

type AnalysisState = "idle" | "analyzing" | "ready" | "fallback" | "error";

interface AnalyzeRolePlan {
  name: string;
  azureVoice?: string;
  azureLang?: string;
  tone?: ToneKey;
  azureStyle?: string;
  azureRole?: string;
  styleDegree?: number;
  rate?: number;
  pitch?: number;
  volume?: number;
  reason?: string;
}

interface AnalyzeSegmentPlan {
  id?: string;
  role?: string;
  speaker?: string;
  name?: string;
  text?: string;
  content?: string;
  line?: string;
  lang?: string;
  language?: string;
  tone?: ToneKey;
  rate?: number;
}

interface AnalyzeResponse {
  source?: "apis-gemini" | "fallback";
  model?: string;
  message?: string;
  segments?: AnalyzeSegmentPlan[];
  roles?: AnalyzeRolePlan[];
}

interface StructuredScript {
  text: string;
  segments: Segment[];
}

interface PendingAudioRequest {
  autoPlay?: boolean;
  readyMessage?: string;
  mode?: AudioRequestMode;
  startIndex?: number;
  startText?: string;
}

interface LeadingDirective {
  rest: string;
  role?: string;
  tone?: ToneKey;
  lang?: string;
  rate?: number;
}

interface InferredSpeaker {
  key: string;
  name: string;
}

interface ScriptSpeakerMatch extends InferredSpeaker {
  rest: string;
  tone?: ToneKey;
}

const STORAGE_KEYS = {
  text: "voice-reader:text",
  roles: "voice-reader:roles",
  settings: "voice-reader:settings",
  pronunciation: "voice-reader:pronunciation"
};

const DEFAULT_ROLE = "旁白";

const SAMPLE_TEXT = `旁白：暮野裡的池面還帶著薄霧，第一句話先慢慢浮上來。
阿言（溫和）：今天我們試著用不同角色朗讀一段文本。
Lina: Bonjour, je peux lire cette phrase avec une voix française.
Kenji（明亮）：日本語の文章も、そのまま読み上げます。
Maya: The pace can be adjusted while the queue keeps its order.

[角色=旁白 語氣=沉靜 速度=0.92] 重行、長安、人參這類多音字或專名，可以在讀音修正裡手動指定讀法。`;

const ROLE_COLORS = [
  "#6f4aa6",
  "#c75d4b",
  "#227e86",
  "#d48624",
  "#4f7d45",
  "#1d6fc7",
  "#8a68c7",
  "#9a6b2f",
  "#b8436f",
  "#2f7a63"
];

const TONES: Record<ToneKey, ToneProfile> = {
  neutral: { label: "平衡", rate: 1, pitch: 1, volume: 1 },
  warm: { label: "溫和", rate: 0.95, pitch: 0.96, volume: 0.98 },
  gentle: { label: "輕柔", rate: 0.9, pitch: 1.05, volume: 0.86 },
  bright: { label: "明亮", rate: 1.08, pitch: 1.14, volume: 1 },
  calm: { label: "沉靜", rate: 0.88, pitch: 0.9, volume: 0.94 },
  serious: { label: "凝重", rate: 0.84, pitch: 0.78, volume: 1 },
  dramatic: { label: "戲劇", rate: 0.96, pitch: 1.22, volume: 1 },
  brisk: { label: "清晰", rate: 1.18, pitch: 1.02, volume: 1 },
  whisper: { label: "低聲", rate: 0.78, pitch: 0.82, volume: 0.68 },
  sad: { label: "低落", rate: 0.82, pitch: 0.86, volume: 0.84 },
  angry: { label: "強烈", rate: 1.08, pitch: 1.06, volume: 1 },
  hopeful: { label: "期盼", rate: 0.96, pitch: 1.1, volume: 0.98 },
  empathetic: { label: "關切", rate: 0.9, pitch: 0.98, volume: 0.92 },
  lyrical: { label: "詩意", rate: 0.82, pitch: 1.12, volume: 0.9 },
  newscast: { label: "播報", rate: 1.05, pitch: 0.98, volume: 1 },
  assistant: { label: "助理", rate: 1.03, pitch: 1, volume: 0.96 }
};

const TONE_ALIASES: Record<string, ToneKey> = {
  neutral: "neutral",
  平衡: "neutral",
  自然: "neutral",
  warm: "warm",
  溫和: "warm",
  温和: "warm",
  柔和: "warm",
  gentle: "gentle",
  soft: "gentle",
  輕柔: "gentle",
  轻柔: "gentle",
  bright: "bright",
  明亮: "bright",
  活潑: "bright",
  活泼: "bright",
  calm: "calm",
  沉靜: "calm",
  沉静: "calm",
  serious: "serious",
  凝重: "serious",
  嚴肅: "serious",
  严肃: "serious",
  dramatic: "dramatic",
  戲劇: "dramatic",
  戏剧: "dramatic",
  激昂: "dramatic",
  brisk: "brisk",
  清晰: "brisk",
  快: "brisk",
  whisper: "whisper",
  低聲: "whisper",
  低声: "whisper",
  sad: "sad",
  悲傷: "sad",
  悲伤: "sad",
  低落: "sad",
  angry: "angry",
  憤怒: "angry",
  愤怒: "angry",
  強烈: "angry",
  强烈: "angry",
  hopeful: "hopeful",
  期盼: "hopeful",
  希望: "hopeful",
  empathetic: "empathetic",
  關切: "empathetic",
  关切: "empathetic",
  lyrical: "lyrical",
  詩意: "lyrical",
  诗意: "lyrical",
  newscast: "newscast",
  播報: "newscast",
  播报: "newscast",
  assistant: "assistant",
  助理: "assistant"
};

const LANG_OPTIONS = [
  { value: "auto", label: "自動" },
  { value: "zh-CN", label: "中文" },
  { value: "zh-HK", label: "粵語" },
  { value: "en-US", label: "English" },
  { value: "ja-JP", label: "日本語" },
  { value: "ko-KR", label: "한국어" },
  { value: "fr-FR", label: "Français" },
  { value: "es-ES", label: "Español" },
  { value: "de-DE", label: "Deutsch" },
  { value: "ru-RU", label: "Русский" },
  { value: "ar", label: "العربية" },
  { value: "hi-IN", label: "हिन्दी" }
];

const AZURE_STYLE_OPTIONS = [
  { value: "auto", label: "自動" },
  { value: "none", label: "中性" },
  { value: "friendly", label: "友好" },
  { value: "gentle", label: "輕柔" },
  { value: "calm", label: "沉靜" },
  { value: "cheerful", label: "愉快" },
  { value: "empathetic", label: "同理" },
  { value: "sad", label: "悲傷" },
  { value: "angry", label: "憤怒" },
  { value: "fearful", label: "恐懼" },
  { value: "hopeful", label: "希望" },
  { value: "disgruntled", label: "不滿" },
  { value: "serious", label: "嚴肅" },
  { value: "excited", label: "激動" },
  { value: "whispering", label: "低聲" },
  { value: "poetry-reading", label: "詩歌" },
  { value: "newscast", label: "播報" },
  { value: "chat", label: "對話" },
  { value: "customerservice", label: "客服" },
  { value: "assistant", label: "助理" }
];

const AZURE_STYLE_LABELS = new Map(AZURE_STYLE_OPTIONS.map((option) => [option.value, option.label]));

const AZURE_STYLE_BY_TONE: Partial<Record<ToneKey, string>> = {
  warm: "friendly",
  gentle: "gentle",
  bright: "cheerful",
  calm: "calm",
  serious: "serious",
  dramatic: "excited",
  brisk: "assistant",
  whisper: "whispering",
  sad: "sad",
  angry: "angry",
  hopeful: "hopeful",
  empathetic: "empathetic",
  lyrical: "poetry-reading",
  newscast: "newscast",
  assistant: "assistant"
};

const AZURE_ROLE_OPTIONS = [
  { value: "none", label: "原聲" },
  { value: "Girl", label: "女孩" },
  { value: "Boy", label: "男孩" },
  { value: "YoungAdultFemale", label: "青年女" },
  { value: "YoungAdultMale", label: "青年男" },
  { value: "OlderAdultFemale", label: "成年女" },
  { value: "OlderAdultMale", label: "成年男" },
  { value: "SeniorFemale", label: "年長女" },
  { value: "SeniorMale", label: "年長男" }
];

const DEFAULT_SETTINGS = {
  masterRate: 1,
  masterPitch: 1,
  masterVolume: 0.86,
  autoLang: true,
  outputFormat: "audio-48khz-192kbitrate-mono-mp3",
  breakMs: 220
};

const PREFERRED_AZURE_VOICES: Record<string, string[]> = {
  "en-US": [
    "en-US-GuyNeural",
    "en-US-JennyNeural",
    "en-US-DavisNeural",
    "en-US-AriaNeural",
    "en-US-TonyNeural",
    "en-US-SaraNeural",
    "en-US-ChristopherNeural",
    "en-US-MichelleNeural"
  ],
  "zh-CN": [
    "zh-CN-XiaoxiaoNeural",
    "zh-CN-YunxiNeural",
    "zh-CN-XiaomoNeural",
    "zh-CN-YunyeNeural",
    "zh-CN-YunyangNeural",
    "zh-CN-XiaoyiNeural"
  ],
  "zh-HK": ["zh-HK-HiuMaanNeural", "zh-HK-WanLungNeural", "zh-HK-HiuGaaiNeural"],
  "zh-TW": ["zh-TW-HsiaoChenNeural", "zh-TW-YunJheNeural", "zh-TW-HsiaoYuNeural"],
  "ja-JP": ["ja-JP-NanamiNeural", "ja-JP-KeitaNeural", "ja-JP-AoiNeural", "ja-JP-DaichiNeural"],
  "ko-KR": ["ko-KR-SunHiNeural", "ko-KR-InJoonNeural"],
  "fr-FR": ["fr-FR-DeniseNeural", "fr-FR-HenriNeural", "fr-FR-EloiseNeural"],
  "es-ES": ["es-ES-ElviraNeural", "es-ES-AlvaroNeural"],
  "de-DE": ["de-DE-KatjaNeural", "de-DE-ConradNeural", "de-DE-AmalaNeural"],
  "ru-RU": ["ru-RU-SvetlanaNeural", "ru-RU-DmitryNeural", "ru-RU-DariyaNeural"],
  "ar-EG": ["ar-EG-SalmaNeural", "ar-EG-ShakirNeural"],
  "hi-IN": ["hi-IN-SwaraNeural", "hi-IN-MadhurNeural"]
};

const MALE_NAME_HINTS = [
  "peter",
  "fu xing",
  "fuxing",
  "jack",
  "tom",
  "john",
  "paul",
  "david",
  "michael",
  "robert",
  "kenji",
  "liam",
  "leo",
  "boy",
  "男",
  "父",
  "先生",
  "哥哥",
  "弟弟"
];

const FEMALE_NAME_HINTS = [
  "alice",
  "lina",
  "maya",
  "jenny",
  "emma",
  "anna",
  "sara",
  "girl",
  "女",
  "母",
  "小姐",
  "姐姐",
  "妹妹"
];

const DEFAULT_PROVIDER_STATUS: ProviderStatus = {
  azure: {
    configured: false,
    quotaGuard: false,
    regionConfigured: false
  },
  limits: {
    maxPerRequest: 3000,
    monthlyLimit: 450000,
    usedThisMonth: 0,
    remainingThisMonth: 450000
  },
  cloudflareWorkersAi: {
    configured: false,
    enabled: false
  }
};

function readJson<T>(key: string, fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}

function safeWriteJson(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Local storage may be blocked; speech still works without persistence.
  }
}

function normalizeTone(value?: string): ToneKey | undefined {
  if (!value) return undefined;
  const compact = value.trim().toLowerCase();
  return TONE_ALIASES[compact] ?? TONE_ALIASES[value.trim()];
}

function parseNumber(value?: string) {
  if (!value) return undefined;
  const parsed = Number(value.replace("%", ""));
  if (!Number.isFinite(parsed)) return undefined;
  return value.includes("%") ? parsed / 100 : parsed;
}

function parseLeadingDirective(line: string): LeadingDirective {
  let rest = line.trim();
  const directive: LeadingDirective = { rest };

  while (rest.startsWith("[")) {
    const end = rest.indexOf("]");
    if (end === -1) break;
    const content = rest.slice(1, end).trim();
    rest = rest.slice(end + 1).trim();

    const parts = content
      .split(/[|,，;；\s]+/)
      .map((part) => part.trim())
      .filter(Boolean);

    parts.forEach((part, index) => {
      const match = part.match(/^([^:=：]+)[:=：](.+)$/);
      if (!match) {
        if (index === 0 && !directive.role) directive.role = part;
        else directive.tone = normalizeTone(part) ?? directive.tone;
        return;
      }

      const key = match[1].trim().toLowerCase();
      const value = match[2].trim();

      if (["role", "speaker", "角色", "人物"].includes(key)) {
        directive.role = value;
      } else if (["tone", "mood", "style", "語氣", "语气"].includes(key)) {
        directive.tone = normalizeTone(value) ?? directive.tone;
      } else if (["lang", "language", "語言", "语言"].includes(key)) {
        directive.lang = value;
      } else if (["rate", "speed", "速度"].includes(key)) {
        directive.rate = parseNumber(value);
      }
    });
  }

  directive.rest = rest;
  return directive;
}

function detectLanguage(text: string) {
  const count = (regex: RegExp) => text.match(regex)?.length ?? 0;
  const kana = count(/[\u3040-\u30ff]/g);
  const hangul = count(/[\uac00-\ud7af]/g);
  const han = count(/[\u3400-\u9fff]/g);
  const arabic = count(/[\u0600-\u06ff]/g);
  const cyrillic = count(/[\u0400-\u04ff]/g);
  const devanagari = count(/[\u0900-\u097f]/g);

  if (kana > 0) return "ja-JP";
  if (hangul > 0) return "ko-KR";
  if (han > 0) return "zh-CN";
  if (arabic > 0) return "ar";
  if (cyrillic > 0) return "ru-RU";
  if (devanagari > 0) return "hi-IN";
  if (/[àâçéèêëîïôûùüÿœ]/i.test(text)) return "fr-FR";
  if (/[áíóúñ¿¡]/i.test(text)) return "es-ES";
  if (/[äöüß]/i.test(text)) return "de-DE";
  return "en-US";
}

function splitSentences(text: string) {
  const normalized = stripFootnoteMarkers(text).replace(/\s+/g, " ").trim();
  if (!normalized) return [];

  const pieces = normalized.match(/[^。！？!?；;…]+[。！？!?；;…]+[\s"'“”‘’」』》〉）)]*|[^。！？!?；;…]+$/g) ?? [normalized];
  return pieces.flatMap((piece) => {
    const sentence = cleanSegmentText(piece);
    if (!sentence) return [];
    if (sentence.length <= 260) return [sentence];
    const chunks = sentence.match(/.{1,220}(?:\s|$)|.{1,220}/g);
    return chunks?.map((chunk) => cleanSegmentText(chunk)).filter(Boolean) ?? [sentence];
  });
}

function stripFootnoteMarkers(value: string) {
  return String(value || "")
    .replace(/[\[［【〔]\s*\d{1,3}\s*[\]］】〕]/g, "")
    .replace(/[（(]\s*\d{1,3}\s*[）)]/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function isQuoteOnly(value: string) {
  return /^[\s"'“”‘’「」『』《》〈〉（）()[\]{}【】〔〕.,，。！？!?；;:：…—-]+$/.test(value);
}

function cleanSegmentText(value: string) {
  return stripFootnoteMarkers(value)
    .replace(/^[\s"'“”‘’「」『』]+/, "")
    .replace(/[\s"'“”‘’「」『』]+$/, "")
    .trim();
}

function comparableSegmentText(value: string) {
  return cleanSegmentText(value).replace(/\s+/g, "");
}

function normalizeAnalyzedSegments(value: AnalyzeSegmentPlan[] | undefined): Segment[] {
  if (!Array.isArray(value)) return [];
  return value
    .slice(0, 80)
    .map((item, index) => {
      const text = cleanSegmentText(String(item.text ?? item.content ?? item.line ?? ""));
      if (!text || isQuoteOnly(text)) return null;
      const role = normalizeSpeakerName(String(item.role ?? item.speaker ?? item.name ?? DEFAULT_ROLE)).slice(0, 24) || DEFAULT_ROLE;
      const lang = String(item.lang ?? item.language ?? detectLanguage(text));
      const tone = item.tone && TONES[item.tone] ? item.tone : undefined;
      const segment: Segment = {
        id: item.id ? String(item.id) : `ai-${index}-${stableHash(`${role}:${text}`)}`,
        role,
        text,
        lang,
        tone
      };
      if (typeof item.rate === "number") segment.rate = Math.min(1.45, Math.max(0.55, item.rate));
      return segment;
    })
    .filter((segment): segment is Segment => Boolean(segment));
}

function normalizeSpeakerName(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function speakerKey(value: string) {
  const normalized = normalizeSpeakerName(value);
  if (/[\u3400-\u9fff]/.test(normalized)) return normalized.replace(/\s+/g, "");
  return normalized.toLowerCase();
}

function isScriptMetaLine(line: string) {
  return !line || /^[（(【\[]/.test(line) || /^[-—]+/.test(line) || /^(剧终|劇終)$/i.test(line);
}

function isLikelyScriptSpeaker(name: string, rest: string) {
  const normalizedName = normalizeSpeakerName(name);
  const key = speakerKey(normalizedName);
  const trimmedRest = rest.trim();
  if (!normalizedName || !key || !trimmedRest) return false;
  if (/^https?$/i.test(normalizedName)) return false;
  if (/[:：\[\]\n]/.test(normalizedName)) return false;
  if (/^[，,。！？!?；;：:）)\]】]/.test(trimmedRest)) return false;

  const hasCjk = /[\u3400-\u9fff]/.test(key);
  return hasCjk ? key.length <= 8 : normalizedName.length <= 24;
}

function colonScriptSpeaker(line: string): ScriptSpeakerMatch | null {
  const normalized = line.trim();
  const match = normalized.match(
    /^([^:：\[\]\n]{1,32})(?:\s*[（(]([^）)]+)[）)])?\s*[:：]\s*(.+)$/
  );
  if (!match) return null;

  const name = normalizeSpeakerName(match[1]);
  const rest = match[3].trim();
  if (!isLikelyScriptSpeaker(name, rest)) return null;
  return { key: speakerKey(name), name, rest, tone: normalizeTone(match[2]) };
}

function spacedScriptSpeaker(line: string): ScriptSpeakerMatch | null {
  const normalized = line.trim();
  if (isScriptMetaLine(normalized)) return null;

  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;

  const maxNameTokens = Math.min(3, parts.length - 1);
  for (let tokenCount = maxNameTokens; tokenCount >= 1; tokenCount -= 1) {
    const nameTokens = parts.slice(0, tokenCount);
    if (tokenCount > 1 && !nameTokens.every((token) => /^[\u3400-\u9fff]$/.test(token))) continue;

    const name = normalizeSpeakerName(nameTokens.join(" "));
    const rest = parts.slice(tokenCount).join(" ").trim();
    if (isLikelyScriptSpeaker(name, rest)) return { key: speakerKey(name), name, rest };
  }

  return null;
}

function recordSpeaker(
  speakers: Map<string, { count: number; explicit: boolean; key: string; name: string }>,
  match: ScriptSpeakerMatch,
  explicit: boolean
) {
  const existing = speakers.get(match.key);
  if (!existing) {
    speakers.set(match.key, { key: match.key, name: match.name, count: 1, explicit });
    return;
  }

  existing.count += 1;
  existing.explicit = existing.explicit || explicit;
}

function inferScriptSpeakers(lines: string[]) {
  const speakers = new Map<string, { count: number; explicit: boolean; key: string; name: string }>();

  lines.forEach((rawLine) => {
    const directive = parseLeadingDirective(rawLine);
    const line = directive.rest;
    const explicitSpeaker = colonScriptSpeaker(line);
    if (explicitSpeaker) {
      recordSpeaker(speakers, explicitSpeaker, true);
      return;
    }

    const spacedSpeaker = spacedScriptSpeaker(line);
    if (spacedSpeaker) recordSpeaker(speakers, spacedSpeaker, false);
  });

  const values = [...speakers.values()];
  const hasExplicit = values.some((speaker) => speaker.explicit);
  let selected = values.filter((speaker) => speaker.explicit || speaker.count >= 2);

  if (!hasExplicit && selected.length < 2) {
    selected = values.filter((speaker) => /[\u3400-\u9fff]/.test(speaker.key));
  }

  if (!hasExplicit && selected.length < 2) return [];
  return selected.sort((a, b) => b.key.length - a.key.length).map(({ key, name }) => ({ key, name }));
}

function speakerPrefixRest(line: string, prefix: string) {
  if (!prefix || line === prefix || !line.startsWith(prefix)) return null;
  const rest = line.slice(prefix.length);
  if (!/^\s|^[（(]/.test(rest)) return null;
  return rest.trim();
}

function preferredSpeakerName(key: string, speakers: InferredSpeaker[]) {
  return speakers.find((speaker) => speaker.key === key)?.name;
}

function matchInferredSpeaker(line: string, speakers: InferredSpeaker[]) {
  const normalized = line.trim();
  if (isScriptMetaLine(normalized)) return null;

  for (const speaker of speakers) {
    const variants = [...new Set([speaker.name, speaker.key])];
    for (const variant of variants) {
      const rest = speakerPrefixRest(normalized, variant);
      if (rest && isLikelyScriptSpeaker(variant, rest)) return { role: speaker.name, rest };
    }
  }

  return null;
}

export function parseSegments(text: string): Segment[] {
  const lines = text.split(/\n+/).map((line) => line.trim()).filter(Boolean);
  const inferredSpeakers = inferScriptSpeakers(lines);
  const segments: Segment[] = [];

  lines.forEach((rawLine, lineIndex) => {
    const directive = parseLeadingDirective(rawLine);
    let line = directive.rest;
    let role = directive.role ?? DEFAULT_ROLE;
    let tone = directive.tone;
    let lang = directive.lang;

    const speaker = colonScriptSpeaker(line);
    if (speaker) {
      role = directive.role ?? preferredSpeakerName(speaker.key, inferredSpeakers) ?? speaker.name;
      tone = tone ?? speaker.tone;
      line = speaker.rest;
    } else {
      const inferredSpeaker = matchInferredSpeaker(line, inferredSpeakers);
      if (inferredSpeaker) {
        role = directive.role ?? inferredSpeaker.role;
        line = inferredSpeaker.rest;
      }
    }

    splitSentences(line).forEach((sentence, sentenceIndex) => {
      segments.push({
        id: `${lineIndex}-${sentenceIndex}-${segments.length}`,
        role,
        text: sentence,
        lang: lang ?? detectLanguage(sentence),
        tone,
        rate: directive.rate
      });
    });
  });

  return segments;
}

function defaultRole(name: string, index: number): RoleConfig {
  const tone: ToneKey = name === DEFAULT_ROLE ? "calm" : "neutral";
  return {
    name,
    color: ROLE_COLORS[index % ROLE_COLORS.length],
    tone,
    rate: 1,
    pitch: 1,
    volume: 0.9,
    azureVoice: "auto",
    azureLang: "auto",
    azureStyle: "auto",
    azureRole: "none",
    styleDegree: 1
  };
}

function preferredLangKey(lang: string) {
  if (PREFERRED_AZURE_VOICES[lang]) return lang;
  const base = lang.split("-")[0];
  if (base === "zh") return "zh-CN";
  if (base === "ar") return "ar-EG";
  return Object.keys(PREFERRED_AZURE_VOICES).find((key) => key.startsWith(`${base}-`)) ?? lang;
}

function roleLanguage(roleName: string, role: RoleConfig, segments: Segment[]) {
  if (role.azureLang && role.azureLang !== "auto") return role.azureLang;
  const counts = new Map<string, number>();
  segments
    .filter((segment) => segment.role === roleName)
    .forEach((segment) => counts.set(segment.lang, (counts.get(segment.lang) ?? 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "en-US";
}

function inferGender(roleName: string, role: RoleConfig): "Female" | "Male" | undefined {
  const rolePlayGender = genderFromAzureRole(role.azureRole);
  if (rolePlayGender) return rolePlayGender;

  const compact = roleName.toLowerCase();
  if (FEMALE_NAME_HINTS.some((hint) => compact.includes(hint))) return "Female";
  if (MALE_NAME_HINTS.some((hint) => compact.includes(hint))) return "Male";
  return undefined;
}

function genderFromAzureRole(rolePlay?: string): "Female" | "Male" | undefined {
  if (/Female|Girl/i.test(rolePlay ?? "")) return "Female";
  if (/Male|Boy/i.test(rolePlay ?? "")) return "Male";
  return undefined;
}

function voiceMatchesGender(voice: AzureVoice | undefined, gender?: "Female" | "Male") {
  return !voice || !gender || voice.gender === gender;
}

function voiceLabel(voice?: AzureVoice) {
  if (!voice) return "";
  return `${voice.localName || voice.displayName || voice.name} · ${voice.locale}`;
}

function isConservativeAutoVoice(voice: AzureVoice) {
  return !/(Turbo|AIGenerate|DragonHD|NeuralHD|OpenAI|PersonalVoice|CustomNeural)/i.test(voice.name);
}

function voiceMatchesLang(voice: AzureVoice, lang: string) {
  const key = preferredLangKey(lang);
  const base = key.split("-")[0];
  return voice.locale === key || voice.locale.startsWith(`${base}-`);
}

function stableHash(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function resolveRoleAzureVoice(
  roleName: string,
  role: RoleConfig,
  index: number,
  segments: Segment[],
  voices: AzureVoice[],
  used: Set<string>
) {
  const requestedGender = genderFromAzureRole(role.azureRole);
  const explicitVoice = role.azureVoice && role.azureVoice !== "auto"
    ? voices.find((voice) => voice.name === role.azureVoice)
    : undefined;
  if (
    explicitVoice &&
    (!requestedGender || explicitVoice.gender === requestedGender) &&
    voiceMatchesLang(explicitVoice, roleLanguage(roleName, role, segments))
  ) {
    return explicitVoice.name;
  }

  const lang = roleLanguage(roleName, role, segments);
  const key = preferredLangKey(lang);
  const preferredNames = PREFERRED_AZURE_VOICES[key] ?? [];
  const preferred = preferredNames
    .map((name) => voices.find((voice) => voice.name === name))
    .filter((voice): voice is AzureVoice => Boolean(voice));
  const matching = voices.filter((voice) => voiceMatchesLang(voice, lang));
  const conservative = matching.filter(isConservativeAutoVoice);
  const basePool = [...preferred, ...(conservative.length ? conservative : matching)].filter(
    (voice, voiceIndex, array) => array.findIndex((item) => item.name === voice.name) === voiceIndex
  );
  const pool = requestedGender ? basePool.filter((voice) => voice.gender === requestedGender) : basePool;
  if (pool.length === 0) return "auto";

  const gender = inferGender(roleName, role);
  const toneStyle = AZURE_STYLE_BY_TONE[role.tone];
  const scored = pool.map((voice, poolIndex) => {
    let score = 0;
    if (!used.has(voice.name)) score += 300;
    if (gender && voice.gender === gender) score += 90;
    if (role.azureRole && role.azureRole !== "none" && voice.roles?.includes(role.azureRole)) score += 80;
    if (toneStyle && voice.styles?.includes(toneStyle)) score += 55;
    const preferredIndex = preferredNames.indexOf(voice.name);
    if (preferredIndex >= 0) score += 60 - preferredIndex * 4;
    if (/Multilingual/i.test(voice.name)) score += 8;
    score += (stableHash(`${roleName}:${voice.name}`) % 17) / 10;
    score -= poolIndex / 100;
    if (used.has(voice.name)) score -= 160 + index;
    return { voice, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.voice.name ?? pool[index % pool.length].name;
}

function buildAnalysisVoiceCatalog(voices: AzureVoice[], segments: Segment[]) {
  const segmentLangs = new Set(segments.map((segment) => preferredLangKey(segment.lang)));
  const baseLangs = new Set([...segmentLangs].map((lang) => lang.split("-")[0]));
  const preferredNames = new Set(
    [...segmentLangs].flatMap((lang) => PREFERRED_AZURE_VOICES[preferredLangKey(lang)] ?? [])
  );

  const scored = voices
    .filter((voice) => voice.freeTier !== false)
    .filter(isConservativeAutoVoice)
    .map((voice) => {
      let score = 0;
      if (segmentLangs.has(voice.locale)) score += 120;
      if (baseLangs.has(voice.locale.split("-")[0])) score += 70;
      if (preferredNames.has(voice.name)) score += 100;
      if (voice.styles?.length) score += 18;
      if (voice.roles?.length) score += 10;
      if (/Multilingual/i.test(voice.name)) score += 6;
      return { voice, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, 180).map(({ voice }) => ({
    name: voice.name,
    locale: voice.locale,
    displayName: voice.localName || voice.displayName || voice.name,
    gender: voice.gender,
    styles: voice.styles ?? [],
    roles: voice.roles ?? [],
    freeTier: voice.freeTier
  }));
}

function formatTime(seconds: number) {
  const safe = Math.max(0, Math.round(seconds));
  const mins = Math.floor(safe / 60)
    .toString()
    .padStart(2, "0");
  const secs = (safe % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
}

function parsePronunciationRules(value: string) {
  return value
    .split(/[\n；;]+/)
    .map((line) => {
      const match = line.match(/^\s*(.{1,64}?)\s*(?:=>|->|=|：|:)\s*(.{1,96}?)\s*$/);
      if (!match) return null;
      return { source: match[1].trim(), alias: match[2].trim() };
    })
    .filter((rule): rule is { source: string; alias: string } => Boolean(rule?.source && rule.alias))
    .slice(0, 80)
    .sort((a, b) => b.source.length - a.source.length);
}

function userFacingAnalysisMessage(message?: string) {
  if (!message) return "Gemini 暫未完成，未套用 AI 音色";
  if (/json|unexpected|parse|syntax|position|non-whitespace|Gemini reply/i.test(message)) {
    return "Gemini 暫未完成，未套用 AI 音色";
  }
  return message;
}

function formatPastedText(input: string) {
  const normalized = input
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();

  if (!normalized) return "";

  return normalized
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) =>
      line.replace(
        /([。！？!?]|\.{1,3})\s+((?:[A-Z][A-Za-z'-]*(?:\s+[A-Z][A-Za-z'-]*){0,3}|[\u3400-\u9fffA-Za-z0-9_·]{1,16})\s*[:：])/g,
        "$1\n$2"
      )
    )
    .join("\n");
}

function renderPronunciationPreview(text: string, rules: ReturnType<typeof parsePronunciationRules>) {
  if (!rules.length) return text;

  const nodes: ReactNode[] = [];
  let index = 0;
  while (index < text.length) {
    const match = rules.find((rule) => text.startsWith(rule.source, index));
    if (match) {
      nodes.push(
        <mark className="pronunciation-mark" key={`${match.source}-${index}`} title={`讀作：${match.alias}`}>
          {match.source}
        </mark>
      );
      index += match.source.length;
    } else {
      nodes.push(text[index]);
      index += 1;
    }
  }
  return nodes;
}

function base64ToUint8Array(value: string) {
  const binary = window.atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function isIosPlaybackEnvironment() {
  if (typeof navigator === "undefined") return false;
  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  return /iPad|iPhone|iPod/.test(userAgent) || (platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function buildAudioSignature(baseSignature: string, mode: AudioRequestMode, segmentIndex: number) {
  return mode === "full" ? baseSignature : `${baseSignature}\u0002segment:${segmentIndex}`;
}

function audioSignatureMatchesText(audioSignature: string, baseSignature: string) {
  return audioSignature === baseSignature || audioSignature.startsWith(`${baseSignature}\u0002segment:`);
}

function primeWebAudioPlayback(context: AudioContext | null) {
  if (!context) return;
  try {
    const source = context.createBufferSource();
    source.buffer = context.createBuffer(1, 1, 22050);
    source.connect(context.destination);
    source.start(0);
  } catch {
    // iOS may expose AudioContext before it is fully unlocked.
  }
}

export default function App() {
  const [text, setText] = useState(() => {
    if (typeof window === "undefined") return SAMPLE_TEXT;
    const saved = window.localStorage.getItem(STORAGE_KEYS.text);
    return saved && !saved.includes("瀏覽器聲音") && !saved.includes("浏览器声音")
      ? saved
      : SAMPLE_TEXT;
  });
  const [roles, setRoles] = useState<Record<string, RoleConfig>>(() => {
    if (typeof window === "undefined") return {};
    return readJson<Record<string, RoleConfig>>(STORAGE_KEYS.roles, {});
  });
  const [structuredScript, setStructuredScript] = useState<StructuredScript | null>(null);
  const [settings, setSettings] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_SETTINGS;
    return readJson(STORAGE_KEYS.settings, DEFAULT_SETTINGS);
  });
  const [pronunciationRules, setPronunciationRules] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(STORAGE_KEYS.pronunciation) ?? "";
  });
  const [azureVoices, setAzureVoices] = useState<AzureVoice[]>([]);
  const [azureConfigured, setAzureConfigured] = useState(false);
  const [providerStatus, setProviderStatus] = useState<ProviderStatus>(DEFAULT_PROVIDER_STATUS);
  const [exportState, setExportState] = useState<"idle" | "generating" | "ready" | "error">("idle");
  const [exportMessage, setExportMessage] = useState("Azure Speech 未檢測");
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [playbackUrls, setPlaybackUrls] = useState<string[]>([]);
  const [playbackSegmentIndex, setPlaybackSegmentIndex] = useState(0);
  const [audioSignature, setAudioSignature] = useState("");
  const [audioTime, setAudioTime] = useState({ current: 0, duration: 0 });
  const [generationProgress, setGenerationProgress] = useState(0);
  const [playbackState, setPlaybackState] = useState<PlaybackState>("idle");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [status, setStatus] = useState("Azure 待機");
  const [analysisState, setAnalysisState] = useState<AnalysisState>("idle");
  const [analysisMessage, setAnalysisMessage] = useState("AI 將自動判斷角色音色");
  const [manuscriptMode, setManuscriptMode] = useState<"edit" | "script">("edit");

  const parsedSegments = useMemo(() => parseSegments(text), [text]);
  const structuredScriptReady = structuredScript?.text === text && structuredScript.segments.length > 0;
  const segments = structuredScriptReady ? structuredScript.segments : parsedSegments;
  const parsedActiveRoles = useMemo(() => {
    const names: string[] = [];
    parsedSegments.forEach((segment) => {
      if (!names.includes(segment.role)) names.push(segment.role);
    });
    return names;
  }, [parsedSegments]);
  const parsedActiveRoleKey = useMemo(() => parsedActiveRoles.join("\u0001"), [parsedActiveRoles]);
  const activeRoles = useMemo(() => {
    const names: string[] = [];
    segments.forEach((segment) => {
      if (!names.includes(segment.role)) names.push(segment.role);
    });
    return names;
  }, [segments]);
  const activeRoleKey = useMemo(() => activeRoles.join("\u0001"), [activeRoles]);
  const autoVoicePlan = useMemo(() => {
    const used = new Set<string>();
    return Object.fromEntries(
      activeRoles.map((roleName, index) => {
        const role = roles[roleName] ?? defaultRole(roleName, index);
        const voice = resolveRoleAzureVoice(roleName, role, index, segments, azureVoices, used);
        if (voice !== "auto") used.add(voice);
        return [roleName, voice];
      })
    );
  }, [activeRoles, azureVoices, roles, segments]);
  const requestSignature = useMemo(
    () => JSON.stringify({ segments, roles, autoVoicePlan, settings, pronunciationRules }),
    [autoVoicePlan, pronunciationRules, roles, segments, settings]
  );
  const activeSegment = segments[Math.min(Math.max(currentIndex, 0), Math.max(segments.length - 1, 0))];
  const activeSegmentRole = activeSegment
    ? roles[activeSegment.role] ?? defaultRole(activeSegment.role, Math.max(0, activeRoles.indexOf(activeSegment.role)))
    : undefined;
  const iosPlaybackEnvironment = isIosPlaybackEnvironment();
  const playbackSource = iosPlaybackEnvironment ? audioUrl : playbackUrls[playbackSegmentIndex] ?? audioUrl;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pendingAutoplayRef = useRef(false);
  const continuePlaybackRef = useRef(false);
  const pendingAudioAfterAnalysisRef = useRef<PendingAudioRequest | null>(null);
  const analysisAbortRef = useRef<AbortController | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const webAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const webAudioTimerRef = useRef<number | null>(null);
  const lastAudioBlobRef = useRef<Blob | null>(null);
  const playbackBlobsRef = useRef<Blob[]>([]);
  const playbackSegmentMapRef = useRef<number[]>([]);
  const audioRequestModeRef = useRef<AudioRequestMode>("full");

  const stopWebAudio = useCallback((nextState?: PlaybackState) => {
    if (webAudioTimerRef.current) {
      window.clearInterval(webAudioTimerRef.current);
      webAudioTimerRef.current = null;
    }
    if (webAudioSourceRef.current) {
      try {
        webAudioSourceRef.current.stop();
      } catch {
        // The source may have already ended.
      }
      webAudioSourceRef.current.disconnect();
      webAudioSourceRef.current = null;
    }
    if (nextState) setPlaybackState(nextState);
  }, []);

  const ensureAudioContext = useCallback(() => {
    const AudioContextCtor =
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return null;
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextCtor();
    }
    if (audioContextRef.current.state === "suspended") {
      void audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  const playSegmentBlobWithWebAudio = useCallback(async (segmentIndex = playbackSegmentIndex) => {
    const blob = playbackBlobsRef.current[segmentIndex] ?? playbackBlobsRef.current[0] ?? lastAudioBlobRef.current;
    if (!blob) return false;

    const context = ensureAudioContext();
    if (!context) return false;

    try {
      await context.resume();
      const audioBuffer = await context.decodeAudioData(await blob.arrayBuffer());
      stopWebAudio();

      const source = context.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(context.destination);
      webAudioSourceRef.current = source;
      const startedAt = context.currentTime;
      const originalSegmentIndex = playbackSegmentMapRef.current[segmentIndex] ?? segmentIndex;
      setPlaybackSegmentIndex(segmentIndex);
      setCurrentIndex(Math.min(originalSegmentIndex, Math.max(segments.length - 1, 0)));
      setAudioTime({ current: 0, duration: audioBuffer.duration });

      source.onended = () => {
        if (webAudioSourceRef.current !== source) return;
        const nextIndex = segmentIndex + 1;
        if (audioRequestModeRef.current === "full" && nextIndex < playbackBlobsRef.current.length) {
          stopWebAudio("idle");
          void playSegmentBlobWithWebAudio(nextIndex);
          return;
        }
        stopWebAudio("idle");
        setAudioTime({ current: audioBuffer.duration, duration: audioBuffer.duration });
        setStatus("播放完成");
      };
      source.start(0);
      webAudioTimerRef.current = window.setInterval(() => {
        const current = Math.min(audioBuffer.duration, context.currentTime - startedAt);
        setAudioTime({ current, duration: audioBuffer.duration });
      }, 250);
      setPlaybackState("playing");
      setStatus("Azure 播放中");
      return true;
    } catch {
      return false;
    }
  }, [ensureAudioContext, playbackSegmentIndex, segments.length, stopWebAudio]);

  const startElementPlayback = useCallback((player: HTMLAudioElement, fallbackStatus: string) => {
    let fallbackStarted = false;
    const startFallback = async () => {
      if (fallbackStarted) return;
      fallbackStarted = true;
      player.pause();
      const webAudioStarted = await playSegmentBlobWithWebAudio(playbackSegmentIndex);
      if (!webAudioStarted) {
        setPlaybackState("paused");
        setStatus(fallbackStatus);
      }
    };

    try {
      const playAttempt = player.play();
      playAttempt
        .then(() => {
          setPlaybackState("playing");
          setStatus("Azure 播放中");
          window.setTimeout(() => {
            if (player.paused && player.currentTime < 0.05) void startFallback();
          }, 450);
        })
        .catch(() => {
          void startFallback();
        });

      window.setTimeout(() => {
        if (player.paused && player.currentTime < 0.05) void startFallback();
      }, 900);
    } catch {
      void startFallback();
    }
  }, [playSegmentBlobWithWebAudio, playbackSegmentIndex]);

  useEffect(() => {
    safeWriteJson(STORAGE_KEYS.roles, roles);
  }, [roles]);

  useEffect(() => {
    safeWriteJson(STORAGE_KEYS.settings, settings);
  }, [settings]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEYS.text, text);
    setStructuredScript((current) => (current?.text === text ? current : null));
    pendingAudioAfterAnalysisRef.current = null;
    setAudioSignature("");
    if (analysisState === "ready") {
      setAnalysisState("idle");
      setAnalysisMessage("AI 將自動整理劇本、角色與音色");
    }
  }, [text]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEYS.pronunciation, pronunciationRules);
  }, [pronunciationRules]);

  useEffect(() => {
    setRoles((previous) => {
      const next = { ...previous };
      let changed = false;
      activeRoles.forEach((roleName, index) => {
        if (!next[roleName]) {
          next[roleName] = defaultRole(roleName, index);
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [activeRoles]);

  useEffect(() => {
    let cancelled = false;

    fetch("/api/voices")
      .then(async (response) => {
        if (!response.ok) throw new Error(`voices ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        setAzureVoices(Array.isArray(data.voices) ? data.voices : []);
        setAzureConfigured(Boolean(data.configured));
        setExportMessage(data.configured ? "Azure Speech 已連接" : "需配置 Azure Speech secrets");
        setStatus(data.configured ? "Azure 已連接" : "Azure 待配置");
      })
      .catch(() => {
        if (cancelled) return;
        setAzureConfigured(false);
        setExportMessage("本地預覽未連接 Azure API");
        setStatus("Azure API 未連接");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!text.trim() || parsedSegments.length === 0 || parsedActiveRoles.length === 0) {
      setAnalysisState("idle");
      setAnalysisMessage("輸入文本後，AI 將自動判斷角色音色");
      return undefined;
    }

    if (structuredScriptReady) return undefined;

    if (!azureConfigured || azureVoices.length === 0) {
      setAnalysisState("idle");
      setAnalysisMessage("等待 Azure 聲音列表載入");
      return undefined;
    }

    const timer = window.setTimeout(() => {
      analysisAbortRef.current?.abort();
      const controller = new AbortController();
      analysisAbortRef.current = controller;
      setAnalysisState("analyzing");
      setAnalysisMessage("AI 正在判斷角色、語言、音色與語氣");
      setStatus("AI 分析角色音色");

      fetch("/api/analyze", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          text,
          segments: parsedSegments.slice(0, 80),
          roleNames: parsedActiveRoles,
          roles: Object.fromEntries(
            parsedActiveRoles.map((roleName, index) => [roleName, roles[roleName] ?? defaultRole(roleName, index)])
          ),
          voices: buildAnalysisVoiceCatalog(azureVoices, parsedSegments)
        }),
        signal: controller.signal
      })
        .then(async (response) => {
          if (!response.ok) throw new Error(`analyze ${response.status}`);
          return response.json() as Promise<AnalyzeResponse>;
        })
        .then((data) => {
          if (controller.signal.aborted) return;
          if (data.source !== "apis-gemini") {
            throw new Error(data.message || "Gemini 未完成角色音色判斷");
          }
          const analyzedSegments = normalizeAnalyzedSegments(data.segments);
          if (!analyzedSegments.length) throw new Error("AI 未返回可朗讀劇本");
          const plans = Array.isArray(data.roles) ? data.roles : [];
          if (!plans.length) throw new Error("AI 未返回角色方案");
          const analyzedRoleNames = analyzedSegments.reduce<string[]>((names, segment) => {
            if (!names.includes(segment.role)) names.push(segment.role);
            return names;
          }, []);

          setStructuredScript({ text, segments: analyzedSegments });
          setCurrentIndex(0);

          setRoles((previous) => {
            const next = { ...previous };
            analyzedRoleNames.forEach((roleName, index) => {
              const plan = plans.find((item) => item.name === roleName);
              const base = next[roleName] ?? defaultRole(roleName, index);
              if (!plan) {
                next[roleName] = base;
                return;
              }
              next[roleName] = {
                ...base,
                azureVoice: plan.azureVoice || base.azureVoice,
                azureLang: plan.azureLang || base.azureLang,
                tone: plan.tone && TONES[plan.tone] ? plan.tone : base.tone,
                azureStyle: plan.azureStyle || base.azureStyle,
                azureRole: plan.azureRole || base.azureRole,
                styleDegree: typeof plan.styleDegree === "number" ? plan.styleDegree : base.styleDegree,
                rate: typeof plan.rate === "number" ? plan.rate : base.rate,
                pitch: typeof plan.pitch === "number" ? plan.pitch : base.pitch,
                volume: typeof plan.volume === "number" ? plan.volume : base.volume,
                analysisSource: "gemini",
                analysisReason: plan.reason || data.message || ""
              };
            });
            return next;
          });

          setAnalysisState("ready");
          setAnalysisMessage(`AI 已完成劇本、角色與音色判斷 · ${data.model || "Gemini"}`);
          setStatus("AI 劇本已套用");
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          pendingAudioAfterAnalysisRef.current = null;
          setAnalysisState("error");
          setAnalysisMessage(userFacingAnalysisMessage(error instanceof Error ? error.message : ""));
          setStatus("AI 判斷未完成");
        });
    }, 780);

    return () => {
      window.clearTimeout(timer);
      analysisAbortRef.current?.abort();
    };
  }, [azureConfigured, azureVoices, parsedActiveRoleKey, parsedActiveRoles, parsedSegments, structuredScriptReady, text]);

  useEffect(() => {
    let cancelled = false;

    fetch("/api/status")
      .then(async (response) => {
        if (!response.ok) throw new Error(`status ${response.status}`);
        return response.json();
      })
      .then((data: ProviderStatus) => {
        if (cancelled) return;
        setProviderStatus(data);
      })
      .catch(() => {
        if (cancelled) return;
        setProviderStatus(DEFAULT_PROVIDER_STATUS);
      });

    return () => {
      cancelled = true;
    };
  }, [exportState]);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  useEffect(() => {
    return () => {
      playbackUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [playbackUrls]);

  useEffect(() => {
    return () => {
      if (webAudioTimerRef.current) window.clearInterval(webAudioTimerRef.current);
      try {
        webAudioSourceRef.current?.stop();
      } catch {
        // Ignore cleanup races on unmount.
      }
    };
  }, []);

  useEffect(() => {
    if (exportState !== "generating") return undefined;
    setGenerationProgress((current) => Math.max(current, 8));
    const timer = window.setInterval(() => {
      setGenerationProgress((current) => {
        if (current >= 92) return current;
        const step = current < 45 ? 9 : current < 75 ? 5 : 2;
        return Math.min(92, current + step);
      });
    }, 420);
    return () => window.clearInterval(timer);
  }, [exportState]);

  useEffect(() => {
    if (segments.length === 0) {
      if (currentIndex !== 0) setCurrentIndex(0);
      return;
    }
    if (currentIndex >= segments.length) setCurrentIndex(segments.length - 1);
  }, [currentIndex, segments.length]);

  useEffect(() => {
    if (!playbackSource || (!pendingAutoplayRef.current && !continuePlaybackRef.current)) return;
    pendingAutoplayRef.current = false;
    continuePlaybackRef.current = false;

    window.setTimeout(() => {
      const player = audioRef.current;
      if (player) startElementPlayback(player, "音頻已就緒，點擊播放鍵繼續");
    }, 0);
  }, [playbackSource, startElementPlayback]);

  const updateRole = useCallback((roleName: string, patch: Partial<RoleConfig>) => {
    setRoles((previous) => ({
      ...previous,
      [roleName]: {
        ...(previous[roleName] ?? defaultRole(roleName, activeRoles.indexOf(roleName))),
        ...patch,
        analysisSource: patch.analysisSource ?? "manual"
      }
    }));
  }, [activeRoles]);

  const handleTextPaste = useCallback((event: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = event.clipboardData.getData("text/plain");
    if (!pasted.trim()) return;

    event.preventDefault();
    const formatted = formatPastedText(pasted);
    const target = event.currentTarget;
    const start = target.selectionStart ?? text.length;
    const end = target.selectionEnd ?? start;
    const next = `${text.slice(0, start)}${formatted}${text.slice(end)}`;
    setText(next);

    window.requestAnimationFrame(() => {
      const cursor = start + formatted.length;
      target.selectionStart = cursor;
      target.selectionEnd = cursor;
    });
  }, [text]);

  const resetAudioSettings = useCallback(() => {
    pendingAutoplayRef.current = false;
    continuePlaybackRef.current = false;
    pendingAudioAfterAnalysisRef.current = null;
    setStructuredScript(null);
    stopWebAudio("idle");
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setPlaybackUrls([]);
    setPlaybackSegmentIndex(0);
    setAudioSignature("");
    playbackSegmentMapRef.current = [];
    audioRequestModeRef.current = "full";
    lastAudioBlobRef.current = null;
    playbackBlobsRef.current = [];
    setAudioTime({ current: 0, duration: 0 });
    setGenerationProgress(0);
    setPlaybackState("idle");
    setExportState("idle");
    setExportMessage(azureConfigured ? "Azure Speech 已連接" : "需配置 Azure Speech secrets");
    setStatus("Azure 待機");
    setAnalysisState("idle");
    setAnalysisMessage("AI 將在文本變更後重新判斷角色音色");
    setSettings(DEFAULT_SETTINGS);
    setRoles({});
  }, [audioUrl, azureConfigured, stopWebAudio]);

  const generateAudio = useCallback(async ({
    autoPlay = false,
    readyMessage,
    mode = "full",
    startIndex = 0
  }: {
    autoPlay?: boolean;
    readyMessage?: string;
    mode?: AudioRequestMode;
    startIndex?: number;
  } = {}) => {
    if (segments.length === 0) {
      setExportState("error");
      setExportMessage("沒有可朗讀的文本");
      setStatus("沒有可朗讀的文本");
      return;
    }
    if (startIndex < 0 || startIndex >= segments.length) return;
    if (!structuredScriptReady) {
      pendingAudioAfterAnalysisRef.current = {
        autoPlay,
        readyMessage,
        mode,
        startIndex,
        startText: segments[startIndex]?.text
      };
      setExportState("idle");
      setPlaybackState("idle");
      setAnalysisState(azureConfigured && azureVoices.length > 0 ? "analyzing" : "idle");
      setAnalysisMessage(
        azureConfigured && azureVoices.length > 0
          ? "AI 正在整理劇本、角色與音色，完成後將自動繼續"
          : "等待 Azure 聲音列表載入後再執行 AI 劇本分析"
      );
      setStatus("AI 先整理朗讀劇本");
      return;
    }

    const requestSegments = mode === "segment" ? [segments[startIndex]] : segments;
    const requestSegmentMap = mode === "segment" ? [startIndex] : segments.map((_, index) => index);
    const resolvedSignature = buildAudioSignature(requestSignature, mode, startIndex);

    setExportState("generating");
    setGenerationProgress(8);
    setExportMessage(autoPlay ? "正在合成" : "正在導出");
    setStatus(mode === "segment" ? `第 ${startIndex + 1} 句合成中` : "Azure 合成中");
    setPlaybackState("idle");
    stopWebAudio();
    pendingAutoplayRef.current = autoPlay;
    continuePlaybackRef.current = false;
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    playbackBlobsRef.current = [];
    setPlaybackUrls([]);
    setPlaybackSegmentIndex(0);
    playbackSegmentMapRef.current = requestSegmentMap;
    audioRequestModeRef.current = mode;
    setCurrentIndex(startIndex);
    setAudioSignature("");
    setAudioTime({ current: 0, duration: 0 });

    try {
      const usedVoices = new Set<string>();
      const rolesForExport = Object.fromEntries(
        activeRoles.map((roleName, index) => {
          const role = roles[roleName] ?? defaultRole(roleName, index);
          const resolvedVoice = resolveRoleAzureVoice(roleName, role, index, segments, azureVoices, usedVoices);
          if (resolvedVoice !== "auto") usedVoices.add(resolvedVoice);
          const selectedVoice = azureVoices.find((voice) => voice.name === resolvedVoice);
          const toneStyle = AZURE_STYLE_BY_TONE[role.tone];
          let azureStyle = role.azureStyle;

          if (!azureStyle || azureStyle === "auto") {
            azureStyle =
              selectedVoice?.styles?.includes(toneStyle ?? "") ? toneStyle : "none";
          } else if (
            selectedVoice?.styles?.length &&
            azureStyle !== "none" &&
            !selectedVoice.styles.includes(azureStyle)
          ) {
            azureStyle = selectedVoice.styles.includes(toneStyle ?? "") ? toneStyle : "none";
          }

          const azureRole =
            selectedVoice?.roles?.length && role.azureRole && role.azureRole !== "none"
              ? selectedVoice.roles.includes(role.azureRole)
                ? role.azureRole
                : "none"
              : "none";

          return [roleName, { ...role, azureVoice: resolvedVoice, azureStyle, azureRole }];
        })
      );

      const response = await fetch("/api/tts", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          segments: requestSegments,
          roles: rolesForExport,
          settings,
          pronunciationRules,
          segmented: mode === "full" && !isIosPlaybackEnvironment()
        })
      });

      if (!response.ok) {
        let message = `合成失敗：HTTP ${response.status}`;
        try {
          const data = await response.json();
          if (data.message) message = data.message;
          else if (data.error) message = data.error;
        } catch {
          // Keep the status-based message for HTML 404s during Vite-only preview.
        }
        throw new Error(message);
      }

      const contentType = response.headers.get("content-type") ?? "";
      let blob: Blob;
      if (contentType.includes("application/json")) {
        const data = await response.json() as {
          chunks?: Array<{ audio: string }>;
        };
        const chunkBytes = (data.chunks ?? []).map((chunk) => base64ToUint8Array(chunk.audio));
        if (!chunkBytes.length) throw new Error("合成失敗");
        const segmentBlobs = chunkBytes.map((bytes) => new Blob([bytes], { type: "audio/mpeg" }));
        const segmentUrls = segmentBlobs.map((segmentBlob) => URL.createObjectURL(segmentBlob));
        blob = new Blob(chunkBytes, { type: "audio/mpeg" });
        playbackBlobsRef.current = segmentBlobs;
        playbackSegmentMapRef.current = requestSegmentMap;
        setPlaybackUrls(segmentUrls);
        setPlaybackSegmentIndex(0);
        setCurrentIndex(startIndex);
      } else {
        blob = await response.blob();
        const singleUrl = URL.createObjectURL(blob);
        playbackBlobsRef.current = [blob];
        playbackSegmentMapRef.current = [startIndex];
        setPlaybackUrls([singleUrl]);
        setPlaybackSegmentIndex(0);
        setCurrentIndex(startIndex);
      }
      lastAudioBlobRef.current = blob;
      pendingAutoplayRef.current = autoPlay;
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      setAudioSignature(resolvedSignature);
      setGenerationProgress(100);
      setExportState("ready");
      setExportMessage(readyMessage ?? "MP3 已就緒");
      setStatus(readyMessage ?? (autoPlay ? "音頻已就緒" : "MP3 已就緒"));
    } catch (error) {
      pendingAutoplayRef.current = false;
      stopWebAudio("idle");
      setExportState("error");
      const message = error instanceof Error ? error.message : "合成失敗";
      setExportMessage(message);
      setStatus(message);
    }
  }, [
    activeRoles,
    audioUrl,
    azureVoices,
    azureConfigured,
    pronunciationRules,
    requestSignature,
    roles,
    segments,
    settings,
    structuredScriptReady,
    stopWebAudio
  ]);

  useEffect(() => {
    if (!structuredScriptReady || analysisState !== "ready") return;
    const pending = pendingAudioAfterAnalysisRef.current;
    if (!pending) return;
    pendingAudioAfterAnalysisRef.current = null;

    let startIndex = Math.min(Math.max(pending.startIndex ?? 0, 0), Math.max(segments.length - 1, 0));
    if (pending.mode === "segment" && pending.startText) {
      const requested = comparableSegmentText(pending.startText);
      const mappedIndex = requested
        ? segments.findIndex((segment) => {
            const candidate = comparableSegmentText(segment.text);
            return candidate.includes(requested) || requested.includes(candidate);
          })
        : -1;
      if (mappedIndex >= 0) startIndex = mappedIndex;
    }

    void generateAudio({
      autoPlay: pending.autoPlay,
      readyMessage: pending.readyMessage,
      mode: pending.mode,
      startIndex
    });
  }, [analysisState, generateAudio, segments, structuredScriptReady]);

  const toggleAudio = useCallback(() => {
    if (exportState === "generating") return;
    if (analysisState === "analyzing") {
      setStatus("AI 正在判斷角色音色");
      return;
    }
    if (webAudioSourceRef.current && playbackState === "playing") {
      stopWebAudio("paused");
      setStatus("已暫停");
      return;
    }
    const player = audioRef.current;
    if (!audioUrl || !player || audioSignature !== requestSignature) {
      if (isIosPlaybackEnvironment()) {
        primeWebAudioPlayback(ensureAudioContext());
      }
      void generateAudio({ autoPlay: true, mode: "full", readyMessage: "音頻已就緒，正在播放" });
      return;
    }

    if (player.paused) {
      startElementPlayback(player, "點擊播放鍵繼續");
      return;
    }

    player.pause();
    setPlaybackState("paused");
    setStatus("已暫停");
  }, [
    analysisState,
    audioSignature,
    audioUrl,
    ensureAudioContext,
    exportState,
    generateAudio,
    playbackState,
    requestSignature,
    startElementPlayback,
    stopWebAudio
  ]);

  const seekAudio = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const player = audioRef.current;
    if (!player || !audioTime.duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    player.currentTime = ratio * audioTime.duration;
    setAudioTime((previous) => ({ ...previous, current: player.currentTime }));
  }, [audioTime.duration]);

  const roleColorOf = useCallback(
    (roleName: string) =>
      roles[roleName]?.color ?? defaultRole(roleName, Math.max(0, activeRoles.indexOf(roleName))).color,
    [activeRoles, roles]
  );

  const jumpToSegment = useCallback(
    (index: number) => {
      if (index < 0 || index >= segments.length) return;
      if (exportState === "generating") return;
      if (analysisState === "analyzing") {
        setStatus("AI 正在判斷角色音色");
        return;
      }

      setCurrentIndex(index);
      if (!iosPlaybackEnvironment && audioSignature === requestSignature && playbackUrls.length > 0) {
        const mappedIndex = playbackSegmentMapRef.current.findIndex((segmentIndex) => segmentIndex === index);
        const nextPlaybackIndex = mappedIndex >= 0 ? mappedIndex : Math.min(index, playbackUrls.length - 1);
        const player = audioRef.current;
        continuePlaybackRef.current = true;
        if (player) {
          player.pause();
          player.currentTime = 0;
        }
        setPlaybackSegmentIndex(nextPlaybackIndex);
        setAudioTime({ current: 0, duration: 0 });
        if (nextPlaybackIndex === playbackSegmentIndex && player) {
          window.setTimeout(() => startElementPlayback(player, "點擊播放鍵繼續"), 0);
        }
        return;
      }

      if (isIosPlaybackEnvironment()) {
        primeWebAudioPlayback(ensureAudioContext());
      }
      void generateAudio({
        autoPlay: true,
        mode: "segment",
        startIndex: index,
        readyMessage: `第 ${index + 1} 句已就緒，正在播放`
      });
    },
    [
      analysisState,
      audioSignature,
      ensureAudioContext,
      exportState,
      generateAudio,
      iosPlaybackEnvironment,
      playbackSegmentIndex,
      playbackUrls.length,
      requestSignature,
      segments.length,
      startElementPlayback
    ]
  );

  useEffect(() => {
    if (playbackState === "playing" && segments.length > 0) setManuscriptMode("script");
  }, [playbackState, segments.length]);

  const pronunciationRuleObjects = useMemo(() => parsePronunciationRules(pronunciationRules), [pronunciationRules]);
  const totalChars = segments.reduce((sum, segment) => sum + segment.text.length, 0);
  const exportTooLong = totalChars > providerStatus.limits.maxPerRequest;
  const azureGuarded = azureConfigured && providerStatus.azure.quotaGuard;
  const providerBadge = azureGuarded ? "Azure F0" : azureConfigured ? "待護欄" : "待配置";
  const pronunciationCount = pronunciationRuleObjects.length;
  const estimateSeconds = totalChars / Math.max(7, 12 * settings.masterRate);
  const audioIsCurrent = Boolean(audioUrl && audioSignature === requestSignature);
  const audioMatchesCurrentText = Boolean(audioUrl && audioSignatureMatchesText(audioSignature, requestSignature));
  const showNativeAudioControls = audioMatchesCurrentText && iosPlaybackEnvironment;
  const currentPercent =
    exportState === "generating"
      ? generationProgress
      : audioTime.duration > 0
        ? Math.min(100, (audioTime.current / audioTime.duration) * 100)
        : 0;
  const elapsedLabel =
    exportState === "generating" ? `${Math.round(generationProgress)}%` : audioTime.duration > 0 ? formatTime(audioTime.current) : "00:00";
  const totalLabel = exportState === "generating" ? "Azure" : audioTime.duration > 0 ? formatTime(audioTime.duration) : formatTime(estimateSeconds);
  const playButtonLabel =
    analysisState === "analyzing"
      ? "分析中"
      : exportState === "generating"
      ? "合成中"
      : playbackState === "playing"
        ? "暫停"
        : audioIsCurrent
          ? "播放"
          : "播放";
  const analysisBadge =
    analysisState === "analyzing"
      ? "AI 分析中"
      : analysisState === "ready"
        ? "AI 已判斷"
        : analysisState === "fallback"
          ? "本地音色"
          : analysisState === "error"
            ? "AI 未完成"
            : "AI 待命";
  const statusCopy =
    exportState === "generating"
      ? `Azure 合成 ${Math.round(generationProgress)}%`
      : analysisState === "analyzing"
        ? "AI 分析角色音色"
        : status;
  const audioStateCopy = exportTooLong
    ? "文本過長"
    : exportState === "generating"
      ? `合成 ${Math.round(generationProgress)}%`
      : exportState === "ready"
        ? status
        : analysisState === "analyzing"
          ? "分析中"
          : azureConfigured
            ? "就緒"
            : "待配置";
  const currentSentenceCopy = activeSegment ? `第 ${currentIndex + 1}/${segments.length} 句` : "待文本";
  const lineCount = text.split(/\n/).length;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  const usagePct = Math.min(
    100,
    Math.round((providerStatus.limits.usedThisMonth / Math.max(1, providerStatus.limits.monthlyLimit)) * 100)
  );
  const consoleHeadline =
    exportState === "generating"
      ? "正在合成"
      : playbackState === "playing"
        ? "正在朗讀"
        : analysisState === "analyzing"
          ? "AI 分析中"
          : audioStateCopy;

  return (
    <main className="studio">
      <div className="studio-veil" aria-hidden="true" />

      <header className="masthead">
        <div className="brand">
          <span className="brand-seal" aria-hidden="true">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="sealFill" x1="6" y1="6" x2="42" y2="42" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#6f8f8a" />
                  <stop offset=".52" stopColor="#7d86b2" />
                  <stop offset="1" stopColor="#cb8b74" />
                </linearGradient>
              </defs>
              <circle cx="24" cy="24" r="22.5" fill="url(#sealFill)" stroke="rgba(255,255,255,0.55)" />
              <path
                d="M9.5 29.5c5-8.5 9.4-8.5 14 0 3 5.4 6 6 9 .8"
                stroke="#fffaf0"
                strokeWidth="2.4"
                strokeLinecap="round"
                fill="none"
                opacity="0.96"
              />
              <path
                d="M24 8.5c0 5.2-3.6 8.4-8.8 8.4 5.2 0 8.8 3.2 8.8 8.4 0-5.2 3.6-8.4 8.8-8.4-5.2 0-8.8-3.2-8.8-8.4Z"
                fill="#fffaf0"
                opacity="0.92"
              />
            </svg>
          </span>
          <div className="brand-words">
            <h1>暮野之聲</h1>
            <p>Voice Reader · 印象派朗讀工作室</p>
          </div>
        </div>

        <div className="masthead-status">
          <div className="status-chip">
            <Languages size={15} />
            <span>{azureConfigured ? `${azureVoices.length} 種聲音` : "聲音載入中"}</span>
          </div>
          <div className={`status-chip ${azureConfigured ? "ok" : "wait"}`}>
            <span className="chip-dot" />
            <span>{azureConfigured ? "Azure 已連接" : "Azure 待命"}</span>
          </div>
          {azureConfigured ? (
            <div
              className="status-chip meter"
              title={`本月已用 ${providerStatus.limits.usedThisMonth.toLocaleString()} / ${providerStatus.limits.monthlyLimit.toLocaleString()} 字`}
            >
              <span className="meter-track">
                <span style={{ width: `${usagePct}%` }} />
              </span>
              <span>{usagePct}%</span>
            </div>
          ) : null}
        </div>
      </header>

      <div className="atelier">
        <section className="card manuscript" aria-labelledby="ms-title">
          <div className="card-head">
            <div className="card-title">
              <Quote size={17} />
              <h2 id="ms-title">文本</h2>
            </div>
            <div className="seg-toggle" role="tablist" aria-label="文本視圖">
              <button
                type="button"
                className={manuscriptMode === "edit" ? "active" : ""}
                onClick={() => setManuscriptMode("edit")}
              >
                <PenLine size={14} />
                <span>編輯</span>
              </button>
              <button
                type="button"
                className={manuscriptMode === "script" ? "active" : ""}
                onClick={() => setManuscriptMode("script")}
                disabled={segments.length === 0}
              >
                <ScrollText size={14} />
                <span>劇本</span>
              </button>
            </div>
          </div>

          {manuscriptMode === "edit" ? (
            <div className="ms-body edit">
              <div className="ms-subtools">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={settings.autoLang}
                    onChange={(event) => setSettings((previous) => ({ ...previous, autoLang: event.target.checked }))}
                  />
                  <span className="switch-track">
                    <span className="switch-knob" />
                  </span>
                  <span>語言自動</span>
                </label>
                <div className="link-actions">
                  <button type="button" className="link" onClick={() => setText(SAMPLE_TEXT)}>
                    示例
                  </button>
                  <span className="link-sep">·</span>
                  <button type="button" className="link" onClick={() => setText("")}>
                    清空
                  </button>
                </div>
              </div>

              <textarea
                className="ms-textarea"
                value={text}
                onChange={(event) => setText(event.target.value)}
                onPaste={handleTextPaste}
                spellCheck={false}
                aria-label="朗讀文本"
                placeholder="在此書寫或粘貼文本。用「角色：內容」標註說話人，AI 會為每個角色配上合適的聲音。"
              />

              <label className="pron-field">
                <span className="pron-label">讀音修正</span>
                <input
                  value={pronunciationRules}
                  onChange={(event) => setPronunciationRules(event.target.value)}
                  placeholder="重行=>chong xing；長安=>chang an"
                />
              </label>

              <div className="ms-stats">
                <span>
                  <b>{text.length}</b> 字
                </span>
                <span>
                  <b>{words}</b> 詞
                </span>
                <span>
                  <b>{lineCount}</b> 行
                </span>
                <span>
                  <b>{segments.length}</b> 句
                </span>
                {pronunciationCount > 0 ? (
                  <span className="accent">
                    <b>{pronunciationCount}</b> 讀音修正
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="ms-body script">
              {segments.length === 0 ? (
                <p className="ms-empty">尚無文本。切回「編輯」開始書寫。</p>
              ) : (
                <ol className="script-list">
                  {segments.map((segment, index) => {
                    const color = roleColorOf(segment.role);
                    const active = index === currentIndex;
                    return (
                      <li key={segment.id}>
                        <button
                          type="button"
                          className={`script-line${active ? " active" : ""}`}
                          style={{ "--role": color } as React.CSSProperties}
                          onClick={() => jumpToSegment(index)}
                        >
                          <span className="script-role">
                            <span className="script-dot" />
                            <span className="script-name">{segment.role}</span>
                          </span>
                          <span className="script-text">
                            {renderPronunciationPreview(segment.text, pronunciationRuleObjects)}
                          </span>
                          {active && playbackState === "playing" ? (
                            <span className="script-eq" aria-hidden="true">
                              <i />
                              <i />
                              <i />
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
              <div className="ms-stats compact">
                <span>{activeSegment ? `第 ${currentIndex + 1} / ${segments.length} 句` : "待文本"}</span>
                <span className="muted">點按任意行，可從該處朗讀</span>
              </div>
            </div>
          )}
        </section>

        <section className="card cast" aria-labelledby="cast-title">
          <div className="card-head">
            <div className="card-title">
              <Users size={17} />
              <h2 id="cast-title">角色與聲音</h2>
            </div>
            <span className={`ai-badge ${analysisState}`}>
              {analysisState === "analyzing" ? <Wand2 size={13} className="spin" /> : <Sparkles size={13} />}
              <span>
                {activeRoles.length} 角色 · {analysisBadge}
              </span>
            </span>
          </div>

          <div className="cast-list">
            {activeRoles.length === 0 ? (
              <p className="ms-empty">輸入文本後，這裡會列出每個角色與其聲音。</p>
            ) : (
              activeRoles.map((roleName, index) => {
                const role = roles[roleName] ?? defaultRole(roleName, index);
                const resolvedVoiceName = autoVoicePlan[roleName] ?? role.azureVoice ?? "auto";
                const selectedAzureVoice = azureVoices.find((voice) => voice.name === resolvedVoiceName);
                const availableAzureStyles = selectedAzureVoice?.styles?.length
                  ? [
                      { value: "auto", label: "自動" },
                      { value: "none", label: "中性" },
                      ...selectedAzureVoice.styles.map((style) => ({
                        value: style,
                        label: AZURE_STYLE_LABELS.get(style) ?? style
                      }))
                    ]
                  : AZURE_STYLE_OPTIONS;
                const availableAzureRoles = selectedAzureVoice?.roles?.length
                  ? [
                      { value: "none", label: "原聲" },
                      ...selectedAzureVoice.roles.map((roleValue) => ({
                        value: roleValue,
                        label: AZURE_ROLE_OPTIONS.find((option) => option.value === roleValue)?.label ?? roleValue
                      }))
                    ]
                  : AZURE_ROLE_OPTIONS;
                const requestedGender = genderFromAzureRole(role.azureRole);
                const filteredAzureVoices = azureVoices.filter((voice) => {
                  const langMatch =
                    !role.azureLang ||
                    role.azureLang === "auto" ||
                    voice.locale === role.azureLang ||
                    voice.locale.startsWith(`${role.azureLang.split("-")[0]}-`);
                  return langMatch && voiceMatchesGender(voice, requestedGender);
                });
                const sourceLabel =
                  role.analysisSource === "gemini"
                    ? "AI"
                    : role.analysisSource === "fallback"
                      ? "保底"
                      : role.analysisSource === "manual"
                        ? "手動"
                        : "智能";
                return (
                  <article className="character" key={roleName} style={{ "--role": role.color } as React.CSSProperties}>
                    <div className="character-head">
                      <span className="character-seal">
                        <UserRound size={15} />
                      </span>
                      <div className="character-id">
                        <strong>{roleName}</strong>
                        <em>
                          {selectedAzureVoice
                            ? voiceLabel(selectedAzureVoice)
                            : analysisState === "analyzing"
                              ? "AI 分析中…"
                              : "待分配"}
                        </em>
                      </div>
                      <span className={`src-badge ${role.analysisSource ?? "auto"}`}>{sourceLabel}</span>
                    </div>

                    <div className="character-grid">
                      <label className="ctrl">
                        <span>聲音</span>
                        <select
                          value={role.azureVoice ?? "auto"}
                          onChange={(event) => updateRole(roleName, { azureVoice: event.target.value })}
                        >
                          <option value="auto">跟隨語言</option>
                          {filteredAzureVoices.map((voice) => (
                            <option key={voice.name} value={voice.name}>
                              {voice.localName || voice.displayName || voice.name} · {voice.locale}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="ctrl">
                        <span>語言</span>
                        <select
                          value={role.azureLang ?? "auto"}
                          onChange={(event) => updateRole(roleName, { azureLang: event.target.value, azureVoice: "auto" })}
                        >
                          {LANG_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="ctrl">
                        <span>語氣</span>
                        <select
                          value={role.tone}
                          onChange={(event) => updateRole(roleName, { tone: event.target.value as ToneKey })}
                        >
                          {Object.entries(TONES).map(([key, tone]) => (
                            <option key={key} value={key}>
                              {tone.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="ctrl">
                        <span>風格</span>
                        <select
                          value={role.azureStyle ?? "auto"}
                          onChange={(event) => updateRole(roleName, { azureStyle: event.target.value })}
                        >
                          {availableAzureStyles.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="ctrl">
                        <span>扮演</span>
                        <select
                          value={role.azureRole ?? "none"}
                          onChange={(event) => updateRole(roleName, { azureRole: event.target.value, azureVoice: "auto" })}
                        >
                          {availableAzureRoles.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className="character-sliders">
                      <label className="slider">
                        <span className="slider-label">
                          強度 <b>{(role.styleDegree ?? 1).toFixed(2)}</b>
                        </span>
                        <input
                          type="range"
                          min="0.2"
                          max="2"
                          step="0.01"
                          value={role.styleDegree ?? 1}
                          onChange={(event) => updateRole(roleName, { styleDegree: Number(event.target.value) })}
                        />
                      </label>
                      <label className="slider">
                        <span className="slider-label">
                          速度 <b>{role.rate.toFixed(2)}x</b>
                        </span>
                        <input
                          type="range"
                          min="0.55"
                          max="1.45"
                          step="0.01"
                          value={role.rate}
                          onChange={(event) => updateRole(roleName, { rate: Number(event.target.value) })}
                        />
                      </label>
                      <label className="slider">
                        <span className="slider-label">
                          音高 <b>{role.pitch.toFixed(2)}</b>
                        </span>
                        <input
                          type="range"
                          min="0.55"
                          max="1.55"
                          step="0.01"
                          value={role.pitch}
                          onChange={(event) => updateRole(roleName, { pitch: Number(event.target.value) })}
                        />
                      </label>
                      <label className="slider">
                        <span className="slider-label">
                          音量 <b>{Math.round(role.volume * 100)}%</b>
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={role.volume}
                          onChange={(event) => updateRole(roleName, { volume: Number(event.target.value) })}
                        />
                      </label>
                    </div>

                    {role.analysisReason ? <p className="character-reason">{role.analysisReason}</p> : null}
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section className="card console" aria-labelledby="console-title">
          <h2 id="console-title" className="sr-only">
            播放與導出
          </h2>
          <div className="console-main">
            <div className="transport">
              <button
                className={`play-btn${exportState === "generating" ? " is-busy" : ""}`}
                type="button"
                onClick={toggleAudio}
                disabled={analysisState === "analyzing" || exportState === "generating" || exportTooLong}
                aria-label="播放或暫停"
              >
                {playbackState === "playing" ? (
                  <Pause size={24} fill="currentColor" />
                ) : (
                  <Play size={24} fill="currentColor" />
                )}
              </button>
              <button className="ghost-btn" type="button" onClick={resetAudioSettings} aria-label="重置">
                <RotateCcw size={17} />
              </button>
              <div className="transport-meta">
                <strong>{consoleHeadline}</strong>
                <span>
                  {currentSentenceCopy}
                  {pronunciationCount > 0 ? ` · 讀音修正 ${pronunciationCount}` : ""}
                </span>
              </div>
            </div>

            <div className="scrubber">
              <span className="time">{elapsedLabel}</span>
              <button className="track" type="button" onClick={seekAudio} aria-label="音頻進度">
                <div className="track-fill" style={{ width: `${currentPercent}%` }}>
                  <span className="track-head" />
                </div>
              </button>
              <span className="time">{totalLabel}</span>
            </div>

          <div className="console-actions">
              {audioMatchesCurrentText && audioUrl ? (
                <a className="export-btn ready" href={audioUrl} download="voice-reader.mp3">
                  <Download size={16} />
                  下載 MP3
                </a>
              ) : (
                <button
                  className="export-btn"
                  type="button"
                  onClick={() => generateAudio({ autoPlay: false, mode: "full" })}
                  disabled={analysisState === "analyzing" || exportState === "generating" || exportTooLong}
                >
                  <Download size={16} />
                  {analysisState === "analyzing" ? "分析中" : exportState === "generating" ? "導出中" : "導出 MP3"}
                </button>
              )}
            </div>
          </div>

          {showNativeAudioControls ? <p className="native-audio-status">{status}</p> : null}

          <div className="console-mixer">
            <label className="mix">
              <Gauge size={15} />
              <span>總速</span>
              <input
                type="range"
                min="0.5"
                max="1.8"
                step="0.01"
                value={settings.masterRate}
                onChange={(event) => setSettings((previous) => ({ ...previous, masterRate: Number(event.target.value) }))}
              />
              <b>{settings.masterRate.toFixed(2)}x</b>
            </label>
            <label className="mix">
              <SlidersHorizontal size={15} />
              <span>音高</span>
              <input
                type="range"
                min="0.55"
                max="1.55"
                step="0.01"
                value={settings.masterPitch}
                onChange={(event) => setSettings((previous) => ({ ...previous, masterPitch: Number(event.target.value) }))}
              />
              <b>{settings.masterPitch.toFixed(2)}</b>
            </label>
            <label className="mix">
              <Volume2 size={15} />
              <span>音量</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={settings.masterVolume}
                onChange={(event) => setSettings((previous) => ({ ...previous, masterVolume: Number(event.target.value) }))}
              />
              <b>{Math.round(settings.masterVolume * 100)}%</b>
            </label>
            <label className="mix">
              <AudioLines size={15} />
              <span>停頓</span>
              <input
                type="range"
                min="0"
                max="900"
                step="20"
                value={settings.breakMs ?? DEFAULT_SETTINGS.breakMs}
                onChange={(event) => setSettings((previous) => ({ ...previous, breakMs: Number(event.target.value) }))}
              />
              <b>{settings.breakMs ?? DEFAULT_SETTINGS.breakMs}ms</b>
            </label>
          </div>

          {exportTooLong ? (
            <p className="console-warn">
              文本超出單次免費層保護上限（{providerStatus.limits.maxPerRequest} 字），請拆分後朗讀。
            </p>
          ) : null}

          <audio
            ref={audioRef}
            className={`audio-engine${showNativeAudioControls ? " native-audio" : ""}`}
            src={playbackSource ?? undefined}
            controls
            preload="auto"
            playsInline
            aria-hidden={showNativeAudioControls ? undefined : true}
            tabIndex={showNativeAudioControls ? 0 : -1}
            onLoadedMetadata={(event) =>
              setAudioTime({
                current: event.currentTarget.currentTime,
                duration: Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0
              })
            }
            onTimeUpdate={(event) =>
              setAudioTime({
                current: event.currentTarget.currentTime,
                duration: Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0
              })
            }
            onPlay={() => {
              setPlaybackState("playing");
              setStatus("Azure 播放中");
            }}
            onPause={() => {
              if (!audioRef.current?.ended) {
                setPlaybackState("paused");
                setStatus("已暫停");
              }
            }}
            onEnded={() => {
              if (
                audioRequestModeRef.current === "full" &&
                !iosPlaybackEnvironment &&
                playbackUrls.length > 0 &&
                playbackSegmentIndex < playbackUrls.length - 1
              ) {
                const nextIndex = playbackSegmentIndex + 1;
                const nextSegmentIndex = playbackSegmentMapRef.current[nextIndex] ?? nextIndex;
                continuePlaybackRef.current = true;
                setPlaybackSegmentIndex(nextIndex);
                setCurrentIndex(nextSegmentIndex);
                setAudioTime({ current: 0, duration: 0 });
                setStatus(`第 ${nextSegmentIndex + 1} 句`);
                return;
              }
              setPlaybackState("idle");
              setStatus("播放完成");
            }}
          />
        </section>
      </div>
    </main>
  );
}
